"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { createClient } from "@/lib/supabase/client"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Loader2, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { RATE_PERIODS, formatRate, parseRate, type RatePeriod } from "@/lib/hiring-rate"

// Pill options mirror the SQL CHECK constraints + barber-side filters.
export const POSITION_TYPE_OPTIONS = [
  { value: "full_time",     label: "Full-time" },
  { value: "part_time",     label: "Part-time" },
  { value: "weekends",      label: "Weekends" },
  { value: "flexible",      label: "Flexible" },
  { value: "contract",      label: "Contract" },
]

export const COMPENSATION_OPTIONS = [
  { value: "booth_rental", label: "Booth rental" },
  { value: "commission",   label: "Commission" },
  { value: "both",         label: "Both" },
  { value: "hourly",       label: "Hourly" },
  { value: "salary",       label: "Salary" },
]

export const SPECIALTY_OPTIONS = [
  { value: "fades",            label: "Fades" },
  { value: "beards",           label: "Beards" },
  { value: "color",            label: "Color" },
  { value: "curly_textured",   label: "Curly / textured" },
  { value: "kids",             label: "Kids" },
  { value: "long_hair",        label: "Long hair" },
  { value: "skin_fades",       label: "Skin fades" },
  { value: "razor_work",       label: "Razor work" },
]

export const EXPERIENCE_OPTIONS = [
  { value: "any",     label: "Any experience" },
  { value: "1plus",   label: "1+ years" },
  { value: "3plus",   label: "3+ years" },
  { value: "5plus",   label: "5+ years" },
  { value: "master",  label: "Master" },
]

export const CLIENTELE_OPTIONS = [
  { value: "building",         label: "Building clientele" },
  { value: "established",      label: "Established clientele" },
  { value: "large_following",  label: "Large following" },
  { value: "walk_in_ready",    label: "Walk-in ready" },
]

export interface JobListingFormState {
  id?: string
  title: string
  description: string
  position_type: string[]
  compensation: string
  compensation_details: string
  specialties: string[]
  experience_required: string
  clientele_pref: string[]
  is_published: boolean
  original_announcement_url: string
}

export const emptyJobListing: JobListingFormState = {
  title: "",
  description: "",
  position_type: [],
  compensation: "",
  compensation_details: "",
  specialties: [],
  experience_required: "",
  clientele_pref: [],
  is_published: false,
  original_announcement_url: "",
}

export interface OwnerShopOption {
  id: string
  name: string
}

interface HiringFormProps {
  shopId: string
  ownerId: string
  initial?: JobListingFormState
  mode: "create" | "edit"
  /** All shops the owner has — drives the shop picker on create. Optional;
   *  if omitted (or length <= 1) the picker is hidden and `shopId` is used. */
  shops?: OwnerShopOption[]
}

export function HiringForm({ shopId, ownerId, initial = emptyJobListing, mode, shops }: HiringFormProps) {
  const router = useRouter()
  const supabase = createClient()
  const [form, setForm] = useState<JobListingFormState>(initial)
  const [saving, setSaving] = useState<null | "draft" | "publish" | "delete">(null)
  // Owner can change which shop the posting belongs to when creating. Edit
  // mode is locked to the original shop — moving a posting between shops
  // would be a different operation.
  const [selectedShopId, setSelectedShopId] = useState<string>(shopId)

  // The rate was one free-text box, and it collected what free text collects:
  // "250 wk", "900", "ask". Amount and period are their own controls now, so a
  // posting can't be published with a number and no unit — but the column still
  // stores one string, so nothing migrates and an old listing opens with its
  // own words intact (anything unparseable lands in the note).
  const [rate, setRate] = useState(() => parseRate(initial.compensation_details))
  const setRateField = (patch: Partial<ReturnType<typeof parseRate>>) => {
    setRate(prev => {
      const next = { ...prev, ...patch }
      setForm(f => ({ ...f, compensation_details: formatRate(next) }))
      return next
    })
  }
  const showShopPicker = mode === "create" && (shops?.length ?? 0) > 1

  const togglePill = (key: keyof JobListingFormState, value: string, multi: boolean) => {
    setForm(prev => {
      const cur = prev[key]
      if (multi && Array.isArray(cur)) {
        return { ...prev, [key]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] }
      }
      return { ...prev, [key]: cur === value ? "" : value }
    })
  }

  const handleSave = async (publish: boolean) => {
    if (!form.title.trim()) { toast.error("Add a title"); return }
    setSaving(publish ? "publish" : "draft")
    const payload = {
      shop_id: mode === "create" ? selectedShopId : shopId,
      owner_id: ownerId,
      title: form.title.trim(),
      description: form.description.trim() || null,
      position_type: form.position_type,
      compensation: form.compensation || null,
      compensation_details: form.compensation_details.trim() || null,
      specialties: form.specialties,
      experience_required: form.experience_required || null,
      clientele_pref: form.clientele_pref,
      is_published: publish,
      original_announcement_url: form.original_announcement_url.trim() || null,
    }
    try {
      if (mode === "edit" && form.id) {
        const { error } = await supabase.from("job_listings").update(payload).eq("id", form.id)
        if (error) throw error
        toast.success(publish ? "Posting updated" : "Saved as draft")
      } else {
        const { error } = await supabase.from("job_listings").insert(payload)
        if (error) throw error
        toast.success(publish ? "Posting published" : "Saved as draft")
      }
      router.push(`/hiring`)
      router.refresh()
    } catch (e: any) {
      toast.error(e?.message || "Couldn't save posting")
    } finally {
      setSaving(null)
    }
  }

  // No renew: postings don't expire (migration 1031). A posting is up until
  // the owner fills, unpublishes or deletes it.
  const handleDelete = async () => {
    if (!form.id) return
    if (!confirm("Delete this posting? This can't be undone.")) return
    setSaving("delete")
    try {
      const { error } = await supabase.from("job_listings").delete().eq("id", form.id)
      if (error) throw error
      toast.success("Posting deleted")
      router.push(`/hiring`)
    } catch (e: any) {
      toast.error(e?.message || "Couldn't delete")
      setSaving(null)
    }
  }

  // Reusable pill renderer
  const PillGroup = ({
    options, value, onToggle, multi,
  }: { options: { value: string; label: string }[]; value: string | string[]; onToggle: (v: string) => void; multi: boolean }) => (
    <div className="flex flex-wrap gap-1.5">
      {options.map(opt => {
        const selected = multi
          ? Array.isArray(value) && value.includes(opt.value)
          : value === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onToggle(opt.value)}
            className={`text-[11px] font-light px-3 py-1 rounded-full border transition-colors ${
              selected
                ? "border-foreground bg-foreground text-background"
                : "border-border bg-background text-muted-foreground hover:border-foreground/30"
            }`}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )

  return (
    <div className="space-y-6">
      {showShopPicker && shops && (
        <div className="space-y-2">
          <Label htmlFor="shop">Shop</Label>
          <Select value={selectedShopId} onValueChange={setSelectedShopId}>
            <SelectTrigger id="shop">
              <SelectValue placeholder="Pick a shop" />
            </SelectTrigger>
            <SelectContent>
              {shops.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground">Posting will appear under this shop on /hiring and on its public listing.</p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={form.title}
          onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
          placeholder="e.g. Full-time barber, Tuesday–Saturday"
          maxLength={120}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          rows={5}
          value={form.description}
          onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
          placeholder="What does the role look like? Who are you looking for?"
          maxLength={2000}
          className="resize-none"
        />
      </div>

      <div className="space-y-2">
        <Label>Position type</Label>
        <PillGroup
          options={POSITION_TYPE_OPTIONS}
          value={form.position_type}
          onToggle={v => togglePill("position_type", v, true)}
          multi
        />
      </div>

      <div className="space-y-2">
        <Label>Compensation</Label>
        <PillGroup
          options={COMPENSATION_OPTIONS}
          value={form.compensation}
          onToggle={v => togglePill("compensation", v, false)}
          multi={false}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="rate_amount">Rate</Label>
        <div className="flex gap-2">
          <div className="relative flex-1 min-w-0">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
            <Input
              id="rate_amount"
              inputMode="decimal"
              className="pl-6 tabular-nums"
              value={rate.amount}
              onChange={e => setRateField({ amount: e.target.value.replace(/[^\d.,]/g, "") })}
              placeholder="500"
              maxLength={9}
            />
          </div>
          <Select
            value={rate.period || undefined}
            onValueChange={v => setRateField({ period: v as RatePeriod })}
          >
            <SelectTrigger className="w-[9.5rem] flex-shrink-0" aria-label="Rate period">
              <SelectValue placeholder="per…" />
            </SelectTrigger>
            <SelectContent>
              {RATE_PERIODS.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          id="rate_note"
          value={rate.note}
          onChange={e => setRateField({ note: e.target.value })}
          placeholder="Anything else — e.g. you keep 100% of services"
          maxLength={160}
        />
        {form.compensation_details && (
          <p className="text-xs text-muted-foreground">
            Barbers see: <span className="font-medium text-foreground">{form.compensation_details}</span>
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Specialties</Label>
        <PillGroup
          options={SPECIALTY_OPTIONS}
          value={form.specialties}
          onToggle={v => togglePill("specialties", v, true)}
          multi
        />
      </div>

      <div className="space-y-2">
        <Label>Experience required</Label>
        <PillGroup
          options={EXPERIENCE_OPTIONS}
          value={form.experience_required}
          onToggle={v => togglePill("experience_required", v, false)}
          multi={false}
        />
      </div>

      <div className="space-y-2">
        <Label>Preferred clientele</Label>
        <PillGroup
          options={CLIENTELE_OPTIONS}
          value={form.clientele_pref}
          onToggle={v => togglePill("clientele_pref", v, true)}
          multi
        />
      </div>

      {/* Optional supplemental link — IG post, flyer, etc. Shown on the role
          as "Also on Instagram" (or whichever host) with its brand mark. */}
      <div className="space-y-2">
        <Label htmlFor="original_announcement_url">
          Also posted on <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Input
          id="original_announcement_url"
          type="url"
          inputMode="url"
          value={form.original_announcement_url}
          onChange={e => setForm(p => ({ ...p, original_announcement_url: e.target.value }))}
          placeholder="https://instagram.com/p/…  or  https://yourshop.com"
          maxLength={500}
        />
        <p className="text-[11px] text-muted-foreground">
          Link the Instagram post, TikTok or page where you announced this role. It shows on the posting as “Also on Instagram”.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
        <Button
          variant="outline"
          onClick={() => handleSave(false)}
          disabled={saving !== null}
          className="rounded-full"
        >
          {saving === "draft" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Save as draft
        </Button>
        <Button
          onClick={() => handleSave(true)}
          disabled={saving !== null}
          className="rounded-full"
        >
          {saving === "publish" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {form.is_published ? "Update + keep published" : "Publish"}
        </Button>

        {mode === "edit" && (
          <Button
            variant="ghost"
            onClick={handleDelete}
            disabled={saving !== null}
            className="rounded-full ml-auto text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            {saving === "delete" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Delete
          </Button>
        )}
      </div>
    </div>
  )
}
